import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dropdown,
  Flex,
  H3,
  Icon,
  Modal,
  M,
  Tooltip,
} from 'design-system-web';
import { Input } from 'components';
import { Alert } from 'components/lds';

import styles from './TargetTimesTab.scss';

/** Popup tooltip defaults to z-index 3; modal overlay is 5 — tip must render above it (same tier as `$toast`). */
const TARGET_TIME_FIELD_TOOLTIP_Z_INDEX = 7;

export type DropdownOption = { label: string; value: string };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  formError: string | null;
  categoryItems: DropdownOption[];
  categoryValue: string | null;
  onCategoryChange: (value: string) => void;
  categoryDisabled?: boolean;
  issueItems: DropdownOption[];
  issueDropdownValue: string | null;
  onIssueChange: (value: string) => void;
  issueDisabled: boolean;
  respHours: string;
  onRespHoursChange: (v: string) => void;
  respMinutes: string;
  onRespMinutesChange: (v: string) => void;
  compHours: string;
  onCompHoursChange: (v: string) => void;
  compMinutes: string;
  onCompMinutesChange: (v: string) => void;
  submitLoading: boolean;
  onSubmit: () => void;
};

export function TargetTimesCreateModal({
  isOpen,
  onClose,
  formError,
  categoryItems,
  categoryValue,
  onCategoryChange,
  categoryDisabled = false,
  issueItems,
  issueDropdownValue,
  onIssueChange,
  issueDisabled,
  respHours,
  onRespHoursChange,
  respMinutes,
  onRespMinutesChange,
  compHours,
  onCompHoursChange,
  compMinutes,
  onCompMinutesChange,
  submitLoading,
  onSubmit,
}: Props) {
  const { t } = useTranslation();

  /** Same label + info icon tooltip pattern as the target time rule table headers. */
  const modalLabelWithTooltip = (labelKey: string, tooltipKey: string) => (
    <div className={styles.modalFieldLabel}>
      <span className={styles.thLabel}>
        {t(labelKey)}
        <Tooltip
          TooltipComponent={t(tooltipKey)}
          zIndex={TARGET_TIME_FIELD_TOOLTIP_Z_INDEX}
        >
          <button
            type="button"
            className={styles.infoTrigger}
            aria-label={t(tooltipKey)}
          >
            <Icon name="info-circle" set="FontAwesome" type="far" />
          </button>
        </Tooltip>
      </span>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId="target-times-create-modal"
      closeButtonSize="small"
      size="small"
    >
      <Flex direction="column" gap={2} className={styles.modalBody}>
        <H3>{t`web.admin.serviceRequest.settings.targetTimes.modalTitle`}</H3>
        <M className={styles.modalIntro}>
          {t`web.admin.serviceRequest.settings.targetTimes.modalDisclaimer`}
        </M>
        {formError ? <Alert type="error">{formError}</Alert> : null}

        <div className={styles.modalSection}>
          <Dropdown
            id="target-times-category-select"
            testId="target-times-category-dropdown"
            doTranslation={false}
            className={styles.dropdownField}
            isFullWidth
            items={categoryItems}
            value={categoryValue}
            onChange={item => onCategoryChange(String(item.value))}
            label={t('web.admin.serviceRequest.settings.targetTimes.form.category')}
            placeholder={t(
              'web.admin.serviceRequest.settings.targetTimes.form.categoryPlaceholder'
            )}
            fixedLabel
            isRequired
            menuPosition="fixed"
            disabled={categoryDisabled}
          />
        </div>

        <div className={styles.modalSection}>
          <Dropdown
            id="target-times-issue-select"
            testId="target-times-issue-dropdown"
            doTranslation={false}
            className={styles.dropdownField}
            isFullWidth
            items={issueItems}
            value={issueDropdownValue}
            onChange={item => onIssueChange(String(item.value))}
            label={t('web.admin.serviceRequest.settings.targetTimes.form.issue')}
            placeholder={t(
              'web.admin.serviceRequest.settings.targetTimes.form.issuePlaceholder'
            )}
            fixedLabel
            disabled={issueDisabled}
            menuPosition="fixed"
          />
        </div>

        <div className={styles.modalSection}>
          {modalLabelWithTooltip(
            'web.admin.serviceRequest.settings.targetTimes.form.targetResponseTime',
            'web.admin.serviceRequest.settings.targetTimes.tooltip.response'
          )}
          <div className={styles.timePairRow}>
            <div className={styles.timeInputSlot}>
              <Input
                fieldName="targetTimesModalRespH"
                fixedLabel
                ariaLabel={`${t('web.admin.serviceRequest.settings.targetTimes.form.targetResponseTime')}, ${t('web.admin.serviceRequest.settings.targetTimes.form.hours')}`}
                value={respHours}
                onChange={onRespHoursChange}
                type="number"
                min={0}
                showClear={false}
                testId="target-times-response-hours"
                rightText={t(
                  'web.admin.serviceRequest.settings.targetTimes.form.hoursSuffix'
                )}
              />
            </div>
            <div className={styles.timeInputSlot}>
              <Input
                fieldName="targetTimesModalRespM"
                fixedLabel
                ariaLabel={`${t('web.admin.serviceRequest.settings.targetTimes.form.targetResponseTime')}, ${t('web.admin.serviceRequest.settings.targetTimes.form.minutes')}`}
                value={respMinutes}
                onChange={onRespMinutesChange}
                type="number"
                min={0}
                input={{ max: 59, min: 0 }}
                showClear={false}
                testId="target-times-response-minutes"
                rightText={t(
                  'web.admin.serviceRequest.settings.targetTimes.form.minutesSuffix'
                )}
              />
            </div>
          </div>
        </div>

        <div className={styles.modalSection}>
          {modalLabelWithTooltip(
            'web.admin.serviceRequest.settings.targetTimes.form.targetCompletionTime',
            'web.admin.serviceRequest.settings.targetTimes.tooltip.completion'
          )}
          <div className={styles.timePairRow}>
            <div className={styles.timeInputSlot}>
              <Input
                fieldName="targetTimesModalCompH"
                fixedLabel
                ariaLabel={`${t('web.admin.serviceRequest.settings.targetTimes.form.targetCompletionTime')}, ${t('web.admin.serviceRequest.settings.targetTimes.form.hours')}`}
                value={compHours}
                onChange={onCompHoursChange}
                type="number"
                min={0}
                showClear={false}
                testId="target-times-completion-hours"
                rightText={t(
                  'web.admin.serviceRequest.settings.targetTimes.form.hoursSuffix'
                )}
              />
            </div>
            <div className={styles.timeInputSlot}>
              <Input
                fieldName="targetTimesModalCompM"
                fixedLabel
                ariaLabel={`${t('web.admin.serviceRequest.settings.targetTimes.form.targetCompletionTime')}, ${t('web.admin.serviceRequest.settings.targetTimes.form.minutes')}`}
                value={compMinutes}
                onChange={onCompMinutesChange}
                type="number"
                min={0}
                input={{ max: 59, min: 0 }}
                showClear={false}
                testId="target-times-completion-minutes"
                rightText={t(
                  'web.admin.serviceRequest.settings.targetTimes.form.minutesSuffix'
                )}
              />
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <Button
            variant="primary"
            size="large"
            loading={submitLoading}
            onClick={() => {
              onSubmit();
            }}
            testId="target-times-submit"
          >
            {t`web.admin.serviceRequest.settings.targetTimes.form.submit`}
          </Button>
          <Button variant="secondary" size="large" onClick={onClose}>
            {t`web.admin.serviceRequest.settings.targetTimes.form.cancel`}
          </Button>
        </div>
      </Flex>
    </Modal>
  );
}
